import React from 'react';
import styled, { css } from '@emotion/native';
import { mint } from '@/shared/styles/colors';
import { BodyText, H3 } from '@/shared/components/StyledText';
import Button from '@/shared/components/Button';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import { IsDesktopProps } from '@/shared/styles/types';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { useInstantStylingToggle } from '@/shared/hooks/useInstantStylingToggle';

interface Props {
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const StylistFindClient = ({ setStep }: Props) => {
  const router = useRouter();
  const isDesktop = useIsDesktop();

  const { isInstantStylingActive, toggleInstantStyling } =
    useInstantStylingToggle();

  const onPressTurnAvailabilityOff = () => {
    isInstantStylingActive && toggleInstantStyling();
    setStep((prev) => prev - 1);
  };

  const onPressLearnMore = () => {
    router.push(ROUTES.HELP.path);
  };

  return (
    <ContentContainer isDesktop={isDesktop}>
      <TopContentContainer>
        <SearchingSpinnerContainer>
          <SearchingSpinnerImage
            source={require('@/assets/images/SearchingSpinner.gif')}
          />
        </SearchingSpinnerContainer>
        <Title>Stay Ready</Title>
        <MainText isDesktop={isDesktop} style={{ marginBottom: 16 }}>
          You&apos;ll get a notification when a client matches with you—just tap
          to accept within 60 seconds, or it&apos;ll move to the next stylist.
        </MainText>
        <BoldText isDesktop={isDesktop} style={{ marginBottom: 24 }}>
          The search can take up to 10 minutes; hang tight while we find your
          perfect match
        </BoldText>
        <Button
          variant="tertiary"
          text="Turn Availability OFF"
          onPress={onPressTurnAvailabilityOff}
          size="medium"
          customStyle={turnAvailabilityOffButtonStyle}
        />
      </TopContentContainer>
      <MainText style={{ fontSize: 12 }}>
        Learn more about{' '}
        <BoldLinkText onPress={onPressLearnMore}>Instant Styling</BoldLinkText>
      </MainText>
    </ContentContainer>
  );
};

export default StylistFindClient;

const ContentContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flex: 1,
  width: '100%',
  backgroundColor: mint,
  borderRadius: isDesktop ? 12 : 0,
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 56,
  paddingBottom: 36,
}));

const TopContentContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const Title = styled(H3)({
  textAlign: 'center',
  textTransform: 'uppercase',
  marginBottom: 16,
});

const SearchingSpinnerContainer = styled.View({
  width: 80,
  height: 80,
});

const SearchingSpinnerImage = styled.Image({
  width: '100%',
  height: '100%',
  resizeMode: 'contain',
});

const MainText = styled(BodyText)<IsDesktopProps>(({ isDesktop }) => ({
  maxWidth: 460,
  fontSize: 16,
  lineHeight: 18,
  textAlign: 'center',
  paddingHorizontal: isDesktop ? 0 : 30,
}));

const BoldText = styled(BodyText)<IsDesktopProps>(({ isDesktop }) => ({
  maxWidth: 460,
  fontSize: 16,
  fontWeight: 'bold',
  lineHeight: 18,
  textAlign: 'center',
  paddingHorizontal: isDesktop ? 0 : 30,
}));

const turnAvailabilityOffButtonStyle = css({
  width: 300,
  paddingHorizontal: 24,
  marginBottom: 48,
});

const BoldLinkText = styled(BodyText)({
  fontSize: 12,
  fontWeight: 'bold',
  textDecorationLine: 'underline',
});
